import type { Settings } from '#/global'
import type { ColorSchemePreference, SidebarModePreference, ThemePreference, UserPreferencesResponse } from '@/api/modules/auth'
import { cloneDeep } from 'es-toolkit'
import consoleApi from '@/api/modules/ai-employee-console'
import apiAuth from '@/api/modules/auth'
import newAiEmployeeConsoleApi from '@/api/modules/new-ai-employee-console'
import { DEFAULT_SIDEBAR_MODE_PREFERENCE, DEFAULT_THEME_PREFERENCE, isSidebarModePreference, isThemePreference } from '@/constants/appearance'
// import apiUser from '@/api/modules/user'
import router from '@/router'
import settingsDefault from '@/settings'
import eventBus from '@/utils/eventBus'
import { diffTwoObj, mergeWithoutUndefinedProps } from '@/utils/object'
import { useAiEmployeeConsoleStore } from './aiEmployeeConsole'
import { useNewAiEmployeeConsoleStore } from './newAiEmployeeConsole'

export const useUserStore = defineStore(
  // 唯一ID
  'user',
  () => {
    const settingsStore = useSettingsStore()
    const tabbarStore = useTabbarStore()
    const routeStore = useRouteStore()
    const menuStore = useMenuStore()
    const aiEmployeeConsoleStore = useAiEmployeeConsoleStore()
    const newAiEmployeeConsoleStore = useNewAiEmployeeConsoleStore()

    const account = ref(localStorage.getItem('account') ?? '')
    const username = ref(localStorage.getItem('username') ?? '')
    const displayName = ref(localStorage.getItem('displayName') ?? localStorage.getItem('username') ?? '')
    const token = ref(localStorage.getItem('token') ?? '')
    const avatar = ref(localStorage.getItem('avatar') ?? '')
    const needChangePassword = ref(localStorage.getItem('needChangePassword') === '1')
    const permissions = ref<string[]>([])
    const isLogin = computed(() => {
      if (token.value) {
        return true
      }
      return false
    })

    // 登录
    async function login(data: {
      account: string
      password: string
    }) {
      const res = await apiAuth.login(data)
      localStorage.setItem('account', res.data.account)
      localStorage.setItem('username', res.data.username || '')
      localStorage.setItem('displayName', res.data.display_name || res.data.nickname || res.data.username || '')
      localStorage.setItem('token', res.data.token)
      localStorage.setItem('avatar', res.data.avatar || '')
      account.value = res.data.account
      username.value = res.data.username || ''
      displayName.value = res.data.display_name || res.data.nickname || res.data.username || ''
      token.value = res.data.token
      avatar.value = res.data.avatar || ''
      setNeedChangePassword(Boolean(res.data.need_change_password))
      return res.data
    }

    // 手动登出
    function logout(redirect = router.currentRoute.value.fullPath) {
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      localStorage.removeItem('displayName')
      token.value = ''
      router.push({
        name: 'login',
        query: {
          ...(redirect !== settingsStore.settings.home.fullPath && router.currentRoute.value.name !== 'login' && { redirect }),
        },
      }).then(logoutCleanStatus)
    }
    // 请求登出
    function requestLogout() {
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      localStorage.removeItem('displayName')
      token.value = ''
      setNeedChangePassword(false)
      if (settingsStore.settings.app.loginExpiredMode === 'redirect') {
        router.push({
          name: 'login',
          query: {
            ...(
              router.currentRoute.value.fullPath !== settingsStore.settings.home.fullPath
              && router.currentRoute.value.name !== 'login'
              && {
                redirect: router.currentRoute.value.fullPath,
              }
            ),
          },
        }).then(logoutCleanStatus)
      }
      else {
        eventBus.emit('global-login-again-visible')
      }
    }
    // 登出后清除状态
    function logoutCleanStatus() {
      localStorage.removeItem('account')
      localStorage.removeItem('avatar')
      account.value = ''
      username.value = ''
      displayName.value = ''
      avatar.value = ''
      permissions.value = []
      setNeedChangePassword(false)
      settingsStore.updateSettings({}, true)
      tabbarStore.clean()
      routeStore.removeRoutes()
      menuStore.setActived(0)
      aiEmployeeConsoleStore.reset()
      newAiEmployeeConsoleStore.reset()
    }

    function setNeedChangePassword(value: boolean) {
      needChangePassword.value = value
      if (value) {
        localStorage.setItem('needChangePassword', '1')
      }
      else {
        localStorage.removeItem('needChangePassword')
      }
    }

    // 获取权限
    async function getPermissions() {
      const res = await apiAuth.getPermission()
      permissions.value = res.data.permissions
      if (permissions.value.some(permission => permission.startsWith('crae:ai:'))) {
        void aiEmployeeConsoleStore.preload(consoleApi.getLaunchConfig, true).catch(() => undefined)
        void newAiEmployeeConsoleStore.preload(newAiEmployeeConsoleApi.getLaunchConfig, true).catch(() => undefined)
      }
      else {
        aiEmployeeConsoleStore.reset()
        newAiEmployeeConsoleStore.reset()
      }
    }

    const appearancePreferenceSaving = ref(false)
    const sidebarModePreference = ref<SidebarModePreference>(DEFAULT_SIDEBAR_MODE_PREFERENCE)
    const sidebarShowControlsPreference = ref(false)
    let appearanceRequestVersion = 0

    watch(token, () => {
      // 账号退出或切换后，旧请求的响应不能重新写入当前界面。
      appearanceRequestVersion += 1
      appearancePreferenceSaving.value = false
      sidebarModePreference.value = DEFAULT_SIDEBAR_MODE_PREFERENCE
      sidebarShowControlsPreference.value = false
      settingsStore.setSidebarPreference(DEFAULT_SIDEBAR_MODE_PREFERENCE, false)
    }, { flush: 'sync' })

    function getCurrentAppearancePreference() {
      const colorScheme = settingsStore.currentColorScheme ?? 'light'
      const configuredTheme = colorScheme === 'dark'
        ? settingsStore.settings.app.darkTheme
        : settingsStore.settings.app.lightTheme

      return {
        color_scheme: colorScheme,
        theme: isThemePreference(configuredTheme)
          ? configuredTheme
          : DEFAULT_THEME_PREFERENCE,
        sidebar_mode: sidebarModePreference.value,
        sidebar_show_controls: sidebarShowControlsPreference.value,
      }
    }

    function applySidebarPreference(preference: UserPreferencesResponse, force = false) {
      const mode = isSidebarModePreference(preference.sidebar_mode)
        ? preference.sidebar_mode
        : DEFAULT_SIDEBAR_MODE_PREFERENCE
      const showControls = preference.sidebar_show_controls === true
      settingsStore.setSidebarPreference(
        force || mode !== sidebarModePreference.value ? mode : undefined,
        showControls,
      )
      sidebarModePreference.value = mode
      sidebarShowControlsPreference.value = showControls
    }

    async function loadAppearancePreference(): Promise<boolean> {
      const requestVersion = ++appearanceRequestVersion
      appearancePreferenceSaving.value = true
      try {
        const res = await apiAuth.getPreferences()
        if (requestVersion !== appearanceRequestVersion) {
          return false
        }
        settingsStore.setAppearancePreference(res.data.color_scheme, res.data.theme)
        applySidebarPreference(res.data, true)
        return true
      }
      catch {
        if (requestVersion !== appearanceRequestVersion) {
          return false
        }
        settingsStore.setAppearancePreference('light', DEFAULT_THEME_PREFERENCE)
        applySidebarPreference({
          color_scheme: 'light',
          theme: DEFAULT_THEME_PREFERENCE,
          sidebar_mode: DEFAULT_SIDEBAR_MODE_PREFERENCE,
          sidebar_show_controls: false,
        }, true)
        return false
      }
      finally {
        if (requestVersion === appearanceRequestVersion) {
          appearancePreferenceSaving.value = false
        }
      }
    }

    async function updateAppearancePreference(preference: {
      colorScheme?: ColorSchemePreference
      theme?: ThemePreference
      sidebarMode?: SidebarModePreference
      sidebarShowControls?: boolean
    }): Promise<boolean> {
      if (appearancePreferenceSaving.value) {
        return false
      }

      const previousPreference = getCurrentAppearancePreference()
      const nextPreference = {
        color_scheme: preference.colorScheme ?? previousPreference.color_scheme,
        theme: preference.theme ?? previousPreference.theme,
        sidebar_mode: preference.sidebarMode ?? previousPreference.sidebar_mode,
        sidebar_show_controls: preference.sidebarShowControls ?? previousPreference.sidebar_show_controls,
      }
      const patch = Object.fromEntries(
        Object.entries(nextPreference).filter(([key, value]) => previousPreference[key as keyof UserPreferencesResponse] !== value),
      ) as Partial<UserPreferencesResponse>
      if (Object.keys(patch).length === 0) {
        return true
      }

      const requestVersion = ++appearanceRequestVersion
      const previousDesktopCollapsed = settingsStore.mode === 'pc'
        ? settingsStore.settings.menu.subMenuCollapse
        : settingsStore.subMenuCollapseLastStatus
      appearancePreferenceSaving.value = true
      settingsStore.setAppearancePreference(nextPreference.color_scheme, nextPreference.theme)
      applySidebarPreference(nextPreference)
      try {
        const res = await apiAuth.updatePreferences(patch)
        if (requestVersion !== appearanceRequestVersion) {
          return false
        }
        const savedPreference = { ...nextPreference, ...res.data }
        settingsStore.setAppearancePreference(savedPreference.color_scheme, savedPreference.theme)
        applySidebarPreference(savedPreference)
        return true
      }
      catch {
        if (requestVersion !== appearanceRequestVersion) {
          return false
        }
        settingsStore.setAppearancePreference(
          previousPreference.color_scheme,
          previousPreference.theme,
        )
        applySidebarPreference(previousPreference)
        if (patch.sidebar_mode !== undefined) {
          settingsStore.subMenuCollapseLastStatus = previousDesktopCollapsed
          if (settingsStore.mode === 'pc') {
            settingsStore.settings.menu.subMenuCollapse = previousDesktopCollapsed
          }
        }
        return false
      }
      finally {
        if (requestVersion === appearanceRequestVersion) {
          appearancePreferenceSaving.value = false
        }
      }
    }

    function toggleSidebarModePreference() {
      return updateAppearancePreference({
        sidebarMode: sidebarModePreference.value === 'auto' ? 'expanded' : 'auto',
      })
    }

    function toggleSidebarCollapsePreference() {
      if (appearancePreferenceSaving.value) {
        return
      }
      if (settingsStore.mode === 'mobile' || sidebarModePreference.value === 'auto') {
        settingsStore.toggleSidebarCollapse()
        return
      }
      void updateAppearancePreference({
        sidebarMode: settingsStore.settings.menu.subMenuCollapse ? 'expanded' : 'collapsed',
      })
    }

    // // 修改密码
    // async function editPassword(data: {
    //   password: string
    //   newPassword: string
    // }) {
    //   // await apiUser.passwordEdit(data)
    // }

    // 框架已将可提供给用户配置的选项提取出来，请勿新增其他选项，不需要的选项可以在这里注释掉
    const preferences = ref<Settings.all>({
      app: {
        themeSync: settingsDefault.app.themeSync,
        colorScheme: settingsDefault.app.colorScheme,
        lightTheme: settingsDefault.app.lightTheme,
        darkTheme: settingsDefault.app.darkTheme,
        radius: settingsDefault.app.radius,
        enableColorAmblyopiaMode: settingsDefault.app.enableColorAmblyopiaMode,
        enableProgress: settingsDefault.app.enableProgress,
        defaultLang: settingsDefault.app.defaultLang,
      },
      menu: {
        mode: settingsDefault.menu.mode,
        style: settingsDefault.menu.style,
        enableDark: settingsDefault.menu.enableDark,
        mainMenuClickMode: settingsDefault.menu.mainMenuClickMode,
        subMenuUniqueOpened: settingsDefault.menu.subMenuUniqueOpened,
        subMenuCollapse: settingsDefault.menu.subMenuCollapse,
        subMenuAutoCollapse: settingsDefault.menu.subMenuAutoCollapse,
        enableSubMenuCollapseButton: settingsDefault.menu.enableSubMenuCollapseButton,
      },
      layout: {
        widthMode: settingsDefault.layout.widthMode,
        widthModeScope: settingsDefault.layout.widthModeScope,
      },
      mainPage: {
        enableTransition: settingsDefault.mainPage.enableTransition,
        transitionMode: settingsDefault.mainPage.transitionMode,
      },
      topbar: {
        mode: settingsDefault.topbar.mode,
        switchTabbarAndToolbar: settingsDefault.topbar.switchTabbarAndToolbar,
      },
      tabbar: {
        style: settingsDefault.tabbar.style,
        enableIcon: settingsDefault.tabbar.enableIcon,
        dblclickAction: settingsDefault.tabbar.dblclickAction,
        enableMemory: settingsDefault.tabbar.enableMemory,
      },
      toolbar: {
        breadcrumb: settingsDefault.toolbar.breadcrumb,
        navSearch: settingsDefault.toolbar.navSearch,
        fullscreen: settingsDefault.toolbar.fullscreen,
        pageReload: settingsDefault.toolbar.pageReload,
        colorScheme: settingsDefault.toolbar.colorScheme,
        layout: settingsDefault.toolbar.layout,
      },
      breadcrumb: {
        style: settingsDefault.breadcrumb.style,
        enableMainMenu: settingsDefault.breadcrumb.enableMainMenu,
      },
    })
    // isPreferencesUpdating 用于防止循环更新
    let isPreferencesUpdating = false
    watch(() => settingsStore.settings, (val) => {
      if (!settingsStore.settings.userPreferences.enable) {
        return
      }
      if (!isPreferencesUpdating) {
        isPreferencesUpdating = true
        preferences.value = mergeWithoutUndefinedProps(val, preferences.value)
      }
      else {
        isPreferencesUpdating = false
      }
    }, {
      deep: true,
    })
    watch(preferences, (val) => {
      if (!settingsStore.settings.userPreferences.enable) {
        return
      }
      if (!isPreferencesUpdating) {
        isPreferencesUpdating = true
        settingsStore.updateSettings(cloneDeep(val))
      }
      else {
        isPreferencesUpdating = false
      }
      updatePreferences(cloneDeep(val))
    }, {
      deep: true,
    })
    // isPreferencesInited 用于防止初始化时触发更新
    let isPreferencesInited = false
    // 获取偏好设置
    async function getPreferences() {
      let data: Settings.all = {}
      if (settingsStore.settings.userPreferences.storageTo === 'local') {
        if (localStorage.has('userPreferences')) {
          data = JSON.parse(localStorage.getItem('userPreferences') as string)[account.value] || {}
        }
      }
      else if (settingsStore.settings.userPreferences.storageTo === 'server') {
        // const res = await apiUser.preferences()
        // data = JSON.parse(res.data.preferences || '{}') as Settings.all
      }
      preferences.value = mergeWithoutUndefinedProps(data, preferences.value)
    }
    // 更新偏好设置
    async function updatePreferences(data: Settings.all = {}) {
      if (!isPreferencesInited) {
        isPreferencesInited = true
        return
      }
      if (!isLogin.value) {
        return
      }
      data = diffTwoObj(settingsDefault, data)
      if (settingsStore.settings.userPreferences.storageTo === 'local') {
        const userPreferencesData = localStorage.has('userPreferences') ? JSON.parse(localStorage.getItem('userPreferences') as string) : {}
        userPreferencesData[account.value] = data
        localStorage.setItem('userPreferences', JSON.stringify(userPreferencesData))
      }
      else if (settingsStore.settings.userPreferences.storageTo === 'server') {
        // await apiUser.preferencesEdit(JSON.stringify(data))
      }
    }

    return {
      account,
      username,
      displayName,
      token,
      avatar,
      needChangePassword,
      permissions,
      appearancePreferenceSaving,
      sidebarModePreference,
      sidebarShowControlsPreference,
      toggleSidebarModePreference,
      toggleSidebarCollapsePreference,
      isLogin,
      login,
      logout,
      requestLogout,
      getPermissions,
      loadAppearancePreference,
      updateAppearancePreference,
      setNeedChangePassword,
      // editPassword,
      preferences,
      getPreferences,
      updatePreferences,
    }
  },
)
